// src/ProfileLevel.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './tabs.css';
import './ProfileLevel.css';

const styles = {
  root: {
    width: '100%',
    minHeight: '100%',
    color: '#e5e7eb',
    fontFamily:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    boxSizing: 'border-box',
    padding: '8px 0 40px',
    overflowX: 'hidden',
  },

  card: {
    width: '100%',
    maxWidth: 840,
    boxSizing: 'border-box',
    overflow: 'hidden',
    background:
      'radial-gradient(circle at top left, #1f2937, #020617)',
    borderRadius: 20,
    padding: 24,
    border: '1px solid rgba(148,163,184,0.35)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
  },

  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    marginBottom: 18,
  },

  headerInfo: {
    flex: '1 1 auto',
    minWidth: 0,
  },

  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: 0.4,
    background: 'rgba(251,191,36,0.08)',
    border: '1px solid rgba(251,191,36,0.5)',
    color: '#fde68a',
    marginBottom: 6,
  },

  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
  },

  subtitle: {
    margin: '8px 0 0',
    fontSize: 13,
    color: '#9ca3af',
    maxWidth: 500,
    lineHeight: 1.5,
  },

  levelCircleOuter: {
    flex: '0 0 140px',
    minWidth: 140,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  levelCircle: {
    width: 120,
    height: 120,
    borderRadius: '50%',
    background:
      'conic-gradient(from 220deg, #fbbf24, #f59e0b, #f97316, #facc15)',
    padding: 3,
    boxSizing: 'border-box',
    boxShadow: '0 0 0 4px rgba(15,23,42,0.8)',
  },

  levelCircleInner: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: '50%',
    background:
      'radial-gradient(circle at 30% 0%, #111827 0, #020617 55%, #020617 100%)',
    border: '1px solid rgba(15,23,42,0.85)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },

  lvlLabel: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: '#9ca3af',
  },

  lvlValue: {
    fontSize: 34,
    fontWeight: 800,
    color: '#fbbf24',
    textShadow: '0 0 18px rgba(250,204,21,0.6)',
  },

  progressBlock: {
    marginTop: 12,
    marginBottom: 16,
  },

  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    fontSize: 13,
    marginBottom: 6,
  },

  progressLabel: {
    color: '#d1d5db',
  },

  progressHint: {
    color: '#9ca3af',
    fontSize: 12,
    textAlign: 'right',
  },

  barOuter: {
    width: '100%',
    height: 14,
    boxSizing: 'border-box',
    borderRadius: 999,
    background: 'rgba(15,23,42,0.9)',
    overflow: 'hidden',
    border: '1px solid rgba(55,65,81,0.9)',
  },

  barInner: (pct) => ({
    width: `${pct}%`,
    height: '100%',
    background:
      'linear-gradient(90deg, #fbbf24 0%, #f59e0b 40%, #f97316 100%)',
    boxShadow:
      pct > 0
        ? '0 0 18px rgba(250,204,21,0.45)'
        : 'none',
    transition: 'width .25s ease-out',
  }),

  statsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 12,
    marginBottom: 4,
    fontSize: 13,
  },

  statPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    padding: '7px 11px',
    borderRadius: 999,
    background: 'rgba(15,23,42,0.9)',
    border: '1px solid rgba(55,65,81,0.9)',
    lineHeight: 1.35,
  },

  statIcon: {
    flex: '0 0 16px',
    width: 16,
    height: 16,
  },

  separator: {
    height: 1,
    background:
      'linear-gradient(90deg, transparent, rgba(148,163,184,0.4), transparent)',
    margin: '14px 0 16px',
  },

  bonusTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 6,
  },

  bonusText: {
    fontSize: 13,
    color: '#cbd5f5',
    maxWidth: 660,
    lineHeight: 1.6,
  },

  bonusList: {
    marginTop: 10,
    marginBottom: 0,
    paddingLeft: 20,
    fontSize: 13,
    color: '#9ca3af',
  },

  bonusLi: {
    marginBottom: 5,
    lineHeight: 1.4,
  },

  cardsPreviewWrap: {
    marginTop: 16,
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: 12,
    width: '100%',
    maxWidth: 660,
  },

  cardPrize: {
    minWidth: 0,
    height: 96,
    boxSizing: 'border-box',
    borderRadius: 12,
    border: '1px solid rgba(250,204,21,0.55)',
    background:
      'radial-gradient(circle at top, #1f2937 0, #020617 60%, #020617 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    fontSize: 13,
    fontWeight: 600,
    color: '#e5e7eb',
    textAlign: 'center',
  },

  cardMystery: {
    minWidth: 0,
    height: 96,
    boxSizing: 'border-box',
    borderRadius: 12,
    border: '1px solid rgba(148,163,184,0.55)',
    background:
      'repeating-linear-gradient(135deg, #111827 0, #111827 9px, #272e3f 9px, #272e3f 18px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    fontSize: 26,
    fontWeight: 800,
    color: '#fbbf24',
    textShadow: '0 0 16px rgba(251,191,36,0.7)',
  },
};

export default function ProfileLevel({ username }) {
  const { t: translate } = useTranslation();
  const API_ORIGIN = process.env.REACT_APP_API_URL;

  const [level, setLevel] = useState(1);
  const [levelPoints, setLevelPoints] = useState(0);
  const [nextLevelXP, setNextLevelXP] = useState(100);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!username) return;

    setError('');

    fetch(
      `${API_ORIGIN}/api/auth/level?username=${encodeURIComponent(
        username
      )}`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            translate('profile.level.loadStatusError', { status: response.status })
          );
        }

        return response.json();
      })
      .then((data) => {
        setLevel(Number(data.level || 1));
        setLevelPoints(Number(data.levelPoints || 0));
        setNextLevelXP(Number(data.nextLevelXP || 100));
      })
      .catch((fetchError) => {
        console.error(fetchError);
        setError(
          translate('profile.level.loadError')
        );
      });
  }, [username, API_ORIGIN, translate]);

  const safeNextLevelXP =
    Number(nextLevelXP) > 0
      ? Number(nextLevelXP)
      : 1;

  const pct = Math.max(
    0,
    Math.min(
      100,
      Math.round(
        (Number(levelPoints || 0) /
          safeNextLevelXP) *
          100
      )
    )
  );

  const remaining = Math.max(
    0,
    Math.round(
      Number(nextLevelXP || 0) -
        Number(levelPoints || 0)
    )
  );

  return (
    <div
      className="profile-level-root"
      style={styles.root}
    >
      <div
        className="profile-level-card"
        style={styles.card}
      >
        <div
          className="profile-level-header"
          style={styles.headerRow}
        >
          <div
            className="profile-level-header-info"
            style={styles.headerInfo}
          >
            <div style={styles.badge}>
              <span>{translate('profile.level.badge')}</span>
            </div>

            <h2 style={styles.title}>
              {translate('profile.level.title', { level })}
            </h2>

            <p style={styles.subtitle}>
              {translate('profile.level.subtitle')}
            </p>
          </div>

          <div
            className="profile-level-circle-outer"
            style={styles.levelCircleOuter}
          >
            <div
              className="profile-level-circle"
              style={styles.levelCircle}
            >
              <div
                style={styles.levelCircleInner}
              >
                <span style={styles.lvlLabel}>
                  {translate('profile.level.levelLabel')}
                </span>

                <span style={styles.lvlValue}>
                  {level}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.progressBlock}>
          <div
            className="profile-level-progress-header"
            style={styles.progressHeader}
          >
            <div style={styles.progressLabel}>
              {translate('profile.level.experience')}: <b>{levelPoints}</b> /{' '}
              <b>{nextLevelXP}</b> XP
            </div>

            <div style={styles.progressHint}>
              {remaining > 0
                ? translate('profile.level.remaining', { remaining })
                : translate('profile.level.maximum')}
            </div>
          </div>

          <div style={styles.barOuter}>
            <div style={styles.barInner(pct)} />
          </div>

          <div
            className="profile-level-stats"
            style={styles.statsRow}
          >
            <div
              className="profile-level-stat-pill"
              style={styles.statPill}
            >
              <svg
                viewBox="0 0 24 24"
                style={styles.statIcon}
                aria-hidden="true"
              >
                <defs>
                  <linearGradient
                    id="xp-grad"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop
                      offset="0%"
                      stopColor="#fbbf24"
                    />
                    <stop
                      offset="100%"
                      stopColor="#f97316"
                    />
                  </linearGradient>
                </defs>

                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="url(#xp-grad)"
                />

                <path
                  d="M9 8l3 4-3 4m6-8-3 4 3 4"
                  fill="none"
                  stroke="#111827"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <span>
                {translate('profile.level.xpSources')}
              </span>
            </div>

            <div
              className="profile-level-stat-pill"
              style={styles.statPill}
            >
              <svg
                viewBox="0 0 24 24"
                style={styles.statIcon}
                aria-hidden="true"
              >
                <path
                  d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7m16 0H4m16-4H4m16 0V7a2 2 0 0 0-2-2h-3.5M4 8V7a2 2 0 0 1 2-2h3.5M9.5 5C8.67 5 8 4.33 8 3.5S8.67 2 9.5 2C11 2 12 3.5 12 3.5S13 2 14.5 2 17 3.12 17 4.5 16.33 7 15.5 7H9.5zM12 8v13"
                  fill="none"
                  stroke="#facc15"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <span>
                {translate('profile.level.attemptPerLevel')}
              </span>
            </div>
          </div>
        </div>

        <div style={styles.separator} />

        <div>
          <div style={styles.bonusTitle}>
            {translate('profile.level.bonusTitle')}
          </div>

          <div style={styles.bonusText}>
            {translate('profile.level.bonusText')}
          </div>

          <ul style={styles.bonusList}>
            <li style={styles.bonusLi}>
              🎲 <b>{translate('profile.level.rewards.xpTitle')}</b>{' '}
              {translate('profile.level.rewards.xpText')}
            </li>

            <li style={styles.bonusLi}>
              💰 <b>{translate('profile.level.rewards.moneyTitle')}</b>{' '}
              {translate('profile.level.rewards.moneyText')}
            </li>

            <li style={styles.bonusLi}>
              🎟 <b>{translate('profile.level.rewards.ticketsTitle')}</b>{' '}
              {translate('profile.level.rewards.ticketsText')}
            </li>

            <li style={styles.bonusLi}>
              🎨 <b>{translate('profile.level.rewards.skinsTitle')}</b>{' '}
              {translate('profile.level.rewards.skinsText')}
            </li>
          </ul>

          <div
            className="profile-level-prizes"
            style={styles.cardsPreviewWrap}
          >
            <div
              className="profile-level-prize"
              style={styles.cardPrize}
            >
              XP +10
            </div>

            <div
              className="profile-level-prize"
              style={styles.cardPrize}
            >
              XP +20
            </div>

            <div
              className="profile-level-prize"
              style={styles.cardPrize}
            >
              XP +30
            </div>

            <div
              className="profile-level-prize"
              style={styles.cardPrize}
            >
              $1
            </div>

            <div
              className="profile-level-prize"
              style={styles.cardPrize}
            >
              $2
            </div>

            <div
              className="profile-level-prize"
              style={styles.cardPrize}
            >
              {translate('profile.common.ticket', { value: 1 })}
            </div>

            <div
              className="profile-level-prize"
              style={styles.cardPrize}
            >
              {translate('profile.common.ticket', { value: 2 })}
            </div>

            <div
              className="profile-level-prize"
              style={styles.cardPrize}
            >
              $5
            </div>

            <div
              className="profile-level-prize"
              style={styles.cardPrize}
            >
              {translate('profile.common.ticket', { value: 10 })}
            </div>

            <div
              className="profile-level-prize profile-level-prize--mystery"
              style={styles.cardMystery}
            >
              ?
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              marginTop: 14,
              fontSize: 12,
              color: '#fecaca',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
